import {Component} from 'react';
import {connect} from 'react-redux';
import injectSheet from 'react-jss';

import {Control, ControlGroup} from '-/components/fields/Control';
import PhoneInput from '-/components/fields/PhoneInput';
import BuyerInfoService from '-/services/BuyerInfoService';
import {RadioGroup, Radio} from '-/components/fields/RadioGroup';
import {isMobile, renderFieldErrors} from '-/util/util';
import CallbackService from '-/services/CallbackService';
import ApiService from '-/services/ApiService';
import NavigationService from '-/services/NavigationService';
import TextInput from '-/components/fields/TextInput';
import EmploymentInfoService from '-/services/EmploymentInfoService';
import Form from '-/components/fields/Form';
import Legend from '-/components/fields/Legend';
import Button from '-/components/general/Button';
import {get} from '-/util/lodash';

const styles = theme => ({
    link: {
        color: theme.colors.primary
    }
});

class Callback extends Component {
    constructor(props) {
        super(props);

        this.handleOnSubmitClick = this.handleOnSubmitClick.bind(this);
        this.handleTypeChange = this.handleTypeChange.bind(this);
        this.handlePhoneInputChange = this.handlePhoneInputChange.bind(this);

        this.state = {
            errorMessages: [],
            showErrors: false,
            submitting: false,
            complete: false
        };
    }

    getErrorMessages() {
        const {selected} = this.props;
        let messages = [...this.refs.phone.getErrors('Phone')];
        if (!selected) {
            messages.push('Please select a time');
        }
        return messages;
    }

    handleOnSubmitClick() {
        // We're using a submit button handler here because FB was intercepting our
        // previous form post and preventing default form submission
        const errorMessages = this.getErrorMessages();

        this.setState({
            errorMessages, showErrors: true
        });

        if (!errorMessages.length) {
            this.setState({submitting: true});

            CallbackService.set('phone', this.refs.phone.value);
            ApiService.saveCallbackTimes().then(() => {
                this.setState({
                    submitting: false,
                    complete: true
                });
            });
        }
    }

    handleFieldChange(key, e) {
        const value = key === 'phone' ? e : e.target.value;
        CallbackService.set(key, value);

        setTimeout(() => {
            const {showErrors} = this.state;
            if (showErrors) {
                const errorMessages = this.getErrorMessages();
                this.setState({errorMessages});
            }
        }, 0);
    }

    handleTimeFieldChange(value) {
        CallbackService.set('selected', value);

        setTimeout(() => {
            const {showErrors} = this.state;
            if (showErrors) {
                const errorMessages = this.getErrorMessages();
                this.setState({errorMessages});
            }
        }, 0);
    }

    handleTypeChange(e) {
        const value = get(e, 'target.value');
        const {props} = this;
        const {phone} = props;
        const phoneValue = value ? props[value] : phone;

        CallbackService.set('phoneType', value);
        if (phoneValue) {
            CallbackService.set('phone', phoneValue);
        }
    }

    handlePhoneInputChange(value) {
        this.handleFieldChange('phone', value);
    }

    getPhoneSelect() {
        const {phoneType, phone, homephone, cellphone, employerPhone} = this.props;
        return (
            <ControlGroup labelWidth={200}>
                <Legend>Please select the best number to call you on</Legend>
                <RadioGroup labelWidth={200} vertical={true} label="Phone Type">
                    <Radio name="phoneType" id="phoneType-home" value="homephone" checked={phoneType === 'homephone'} label={`Home ${homephone ? '(' + homephone + ')' : ''}`} onChange={this.handleTypeChange} />
                    <Radio name="phoneType" id="phoneType-cell" value="cellphone" checked={phoneType === 'cellphone'} label={`Cell ${cellphone ? '(' + cellphone + ')' : ''}`} onChange={this.handleTypeChange} />
                    <Radio name="phoneType" id="phoneType-employer" value="employerPhone" checked={phoneType === 'employerPhone'} label={`Employer ${employerPhone ? '(' + employerPhone + ')' : ''}`} onChange={this.handleTypeChange} />
                </RadioGroup>
                <Control>
                    <PhoneInput
                        ref="phone"
                        label="Phone Number"
                        value={phone}
                        onChange={this.handlePhoneInputChange} />
                </Control>
            </ControlGroup>
        );
    }

    formatTime(time) {
        const {useMilitary} = this.props;
        if (useMilitary) {
            return time;
        }

        time = parseInt(time);
        if (time >= 12) {
            if (time > 12) {
                time -= 12;
            }
            return `${time}pm`;
        } else {
            if (time === 0) {
                time = 12;
            }
            return `${time}am`;
        }
    }

    render() {
        const {apiErrors = [], contactPhone, times, selected, timezone, name, classes = {}} = this.props;
        const {complete, errorMessages, showErrors, submitting} = this.state;

        if (complete) {
            let msg = 'We will call you at the time you specified.';
            if (selected === 'Within an hour') {
                msg = <span>We will be with you shortly. If you don’t receive a call or
                    email back within one hour of submission, please call {name} at <a href={`tel:${contactPhone}`}>{contactPhone}</a>.</span>;
            }
            return (
                <Form standalone={true}>
                    <ControlGroup>
                        <Legend>Thank you for requesting a call back</Legend>
                        <p className="info">{msg}</p>
                        <Button onClick={() => NavigationService.goBack()} btnStyle="addRight">Go Back</Button>
                    </ControlGroup>
                </Form>
            );
        }

        return (
            <div>
                <Form standalone={true}>
                    <ControlGroup>
                        <p className="info">Our business hours are Monday - Friday, 9:00am - 5:00pm {timezone}. We will contact you at the time you specify.</p>
                        {contactPhone ? (
                            <p className="info">We will call you from: <a className={classes.link} href={`tel:${contactPhone}`}>{contactPhone}</a></p>
                        ) : null}
                    </ControlGroup>
                    {this.getPhoneSelect()}
                    <ControlGroup labelWidth={200}>
                        <Legend>Your preferred time</Legend>
                        {times.length > 0 ? (
                            <RadioGroup
                                ref="times"
                                vertical={isMobile()}>
                                {times.map((time, i) => {
                                    let label = '';
                                    if (time.now) {
                                        label = 'Within an hour';
                                    } else {
                                        label = `${this.formatTime(time.from)} - ${this.formatTime(time.to)}`;
                                    }
                                    return (
                                        <Radio
                                            name="callbackTimes"
                                            onChange={e => this.handleFieldChange('selected', e)}
                                            checked={selected === label}
                                            key={i}
                                            label={label}
                                            value={label}
                                            id={i} />
                                    );
                                })}
                            </RadioGroup>
                        ) : (
                            <Control>
                                <TextInput
                                    ref="timeInput"
                                    label="Time"
                                    value={selected}
                                    onChange={e => this.handleTimeFieldChange(e)} />
                            </Control>
                        )}
                    </ControlGroup>
                    {showErrors ? renderFieldErrors([...errorMessages, ...apiErrors]) : null}
                    <Button onClick={this.handleOnSubmitClick} btnStyle="addRight" disabled={submitting}>
                        {submitting ? 'Submitting...' : 'Submit'}
                    </Button>
                </Form>
            </div>
        );
    }
}

const mapStateToProps = state => {
    const {config = {}, creditor = {}} = state;
    const {contactPhone} = config;
    const {name = ''} = creditor;
    const {homephone, cellphone} = BuyerInfoService.getBuyerInfoForCurrentApplicant();
    const {employerPhone} = EmploymentInfoService.getEmploymentInfoForCurrentApplicant();
    const {times, selected, useMilitary} = CallbackService.getReducerState();
    let {phoneType, phone, timezone} = CallbackService.getReducerState();
    if (!phoneType) {
        if (homephone) {
            phoneType = 'homephone';
            phone = homephone;
        } else if (cellphone) {
            phoneType = 'cellphone';
            phone = cellphone;
        } else if (employerPhone) {
            phoneType = 'employerPhone';
            phone = employerPhone;
        }
    }

    const apiErrors = ApiService.getErrors();

    return {
        apiErrors,
        contactPhone,
        homephone,
        cellphone,
        employerPhone,
        times,
        selected,
        phone,
        phoneType,
        useMilitary,
        timezone,
        name
    };
};

export default connect(mapStateToProps)(injectSheet(styles)(Callback));
